// src/pages/Stock/UnitsOfMeasurePage.jsx
// =============================================================================
// Units of Measure maintenance page
// =============================================================================
//
// WHY THIS PAGE LIVES UNDER STOCK
// -------------------------------
// The units_of_measure table is shared by Product Catalog and Stock. Products
// use units for content and stock unit fields, but the business reason for
// maintaining this dictionary now is inventory: users need to control how stock
// quantities are counted and displayed.
//
// Important UX rule:
// - units should normally be deactivated, not deleted,
// - unit code is a stable business key and is intentionally not editable after
//   creation,
// - backend validation remains authoritative.

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createUnitOfMeasure,
  listUnitsOfMeasure,
  setUnitOfMeasureActive,
  updateUnitOfMeasure,
} from "../../services/stockMasterDataService";

import "./StockMasterDataPage.css";

const EMPTY_FORM = {
  code: "",
  name_public: "",
  symbol: "",
  dimension: "COUNT",
  decimal_places: 0,
  is_stock_unit: true,
  is_content_unit: true,
  is_active: true,
  sort_order: 0,
};

const DIMENSIONS = ["COUNT", "VOLUME", "MASS", "LENGTH", "TIME", "OTHER"];

function normalizeForm(row = {}) {
  return {
    code: row.code || "",
    name_public: row.name_public || "",
    symbol: row.symbol || "",
    dimension: row.dimension || "COUNT",
    decimal_places: row.decimal_places ?? 0,
    is_stock_unit: row.is_stock_unit !== false,
    is_content_unit: row.is_content_unit !== false,
    is_active: row.is_active !== false,
    sort_order: row.sort_order ?? 0,
  };
}

export default function UnitsOfMeasurePage() {
  const { t } = useTranslation(["stock", "common"]);

  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingCode, setEditingCode] = useState(null);
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const isEditing = Boolean(editingCode);

  async function loadRows() {
    setLoading(true);
    setError(null);
    try {
      const data = await listUnitsOfMeasure({ active: includeInactive ? "" : "true" });
      setRows(data);
    } catch (err) {
      setError(t("stock:masterData.units.messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
    
  }, [includeInactive]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const haystack = [row.code, row.name_public, row.symbol, row.dimension]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, search]);

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    setEditingCode(null);
    setMessage(null);
    setError(null);
  }

  function editRow(row) {
    setForm(normalizeForm(row));
    setEditingCode(row.code);
    setMessage(null);
    setError(null);
  }

  async function submitForm(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      if (isEditing) {
        await updateUnitOfMeasure(editingCode, form);
        setMessage(t("stock:masterData.units.messages.updated"));
      } else {
        await createUnitOfMeasure(form);
        setMessage(t("stock:masterData.units.messages.created"));
      }
      resetForm();
      await loadRows();
    } catch (err) {
      setError(err?.message || t("stock:masterData.units.messages.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(row) {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await setUnitOfMeasureActive(row.code, !row.is_active);
      setMessage(t("stock:masterData.units.messages.statusUpdated"));
      await loadRows();
    } catch (err) {
      setError(err?.message || t("stock:masterData.units.messages.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="stock-md-page">
      <header className="stock-md-header">
        <div>
          <p className="stock-md-eyebrow">{t("stock:masterData.eyebrow")}</p>
          <h1 className="stock-md-title">{t("stock:masterData.units.title")}</h1>
          <p className="stock-md-subtitle">{t("stock:masterData.units.subtitle")}</p>
        </div>
        <button type="button" className="stock-md-button" onClick={loadRows} disabled={loading}>
          {t("common:refresh", { defaultValue: "Refresh" })}
        </button>
      </header>

      <section className="stock-md-info-box">
        {t("stock:masterData.units.guidance")}
      </section>

      {error && <div className="stock-md-error">{error}</div>}
      {message && <div className="stock-md-success">{message}</div>}

      <div className="stock-md-grid">
        <section className="stock-md-card">
          <h2 className="stock-md-card-title">
            {isEditing ? t("stock:masterData.units.form.editTitle") : t("stock:masterData.units.form.createTitle")}
          </h2>
          <p className="stock-md-card-description">{t("stock:masterData.units.form.description")}</p>

          <form onSubmit={submitForm}>
            <div className="stock-md-form-grid">
              <label className="stock-md-form-field">
                <span className="stock-md-label">{t("stock:masterData.units.fields.code")}</span>
                <input
                  className="stock-md-input"
                  value={form.code}
                  onChange={(e) => updateField("code", e.target.value.toLowerCase())}
                  disabled={isEditing || saving}
                  required
                />
                <span className="stock-md-hint">{t("stock:masterData.units.hints.code")}</span>
              </label>

              <label className="stock-md-form-field">
                <span className="stock-md-label">{t("stock:masterData.units.fields.symbol")}</span>
                <input
                  className="stock-md-input"
                  value={form.symbol}
                  onChange={(e) => updateField("symbol", e.target.value)}
                  disabled={saving}
                  required
                />
              </label>

              <label className="stock-md-form-field stock-md-form-field--full">
                <span className="stock-md-label">{t("stock:masterData.units.fields.name")}</span>
                <input
                  className="stock-md-input"
                  value={form.name_public}
                  onChange={(e) => updateField("name_public", e.target.value)}
                  disabled={saving}
                  required
                />
              </label>

              <label className="stock-md-form-field">
                <span className="stock-md-label">{t("stock:masterData.units.fields.dimension")}</span>
                <select
                  className="stock-md-select"
                  value={form.dimension}
                  onChange={(e) => updateField("dimension", e.target.value)}
                  disabled={saving}
                >
                  {DIMENSIONS.map((dimension) => (
                    <option key={dimension} value={dimension}>{dimension}</option>
                  ))}
                </select>
              </label>

              <label className="stock-md-form-field">
                <span className="stock-md-label">{t("stock:masterData.units.fields.decimalPlaces")}</span>
                <input
                  className="stock-md-input"
                  type="number"
                  min="0"
                  max="6"
                  value={form.decimal_places}
                  onChange={(e) => updateField("decimal_places", e.target.value)}
                  disabled={saving}
                />
                <span className="stock-md-hint">{t("stock:masterData.units.hints.decimalPlaces")}</span>
              </label>

              <label className="stock-md-checkbox-row">
                <input
                  type="checkbox"
                  checked={form.is_stock_unit}
                  onChange={(e) => updateField("is_stock_unit", e.target.checked)}
                  disabled={saving}
                />
                {t("stock:masterData.units.fields.stockUnit")}
              </label>

              <label className="stock-md-checkbox-row">
                <input
                  type="checkbox"
                  checked={form.is_content_unit}
                  onChange={(e) => updateField("is_content_unit", e.target.checked)}
                  disabled={saving}
                />
                {t("stock:masterData.units.fields.contentUnit")}
              </label>

              <label className="stock-md-form-field">
                <span className="stock-md-label">{t("stock:masterData.common.sortOrder")}</span>
                <input
                  className="stock-md-input"
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => updateField("sort_order", e.target.value)}
                  disabled={saving}
                />
              </label>

              <label className="stock-md-checkbox-row">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => updateField("is_active", e.target.checked)}
                  disabled={saving}
                />
                {t("stock:masterData.common.active")}
              </label>
            </div>

            <div className="stock-md-actions">
              <button type="button" className="stock-md-button" onClick={resetForm} disabled={saving}>
                {t("common:cancel", { defaultValue: "Cancel" })}
              </button>
              <button type="submit" className="stock-md-button stock-md-button--primary" disabled={saving}>
                {isEditing ? t("stock:masterData.common.saveChanges") : t("stock:masterData.common.create")}
              </button>
            </div>
          </form>
        </section>

        <section className="stock-md-card">
          <div className="stock-md-toolbar">
            <div>
              <h2 className="stock-md-card-title">{t("stock:masterData.units.listTitle")}</h2>
              <p className="stock-md-card-description">{t("stock:masterData.units.listDescription")}</p>
            </div>
            <label className="stock-md-checkbox-row">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              {t("stock:masterData.common.showInactive")}
            </label>
          </div>

          <input
            className="stock-md-input stock-md-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("stock:masterData.units.searchPlaceholder")}
          />

          <div className="stock-md-table-wrap" style={{ marginTop: 12 }}>
            <table className="stock-md-table">
              <thead>
                <tr>
                  <th>{t("stock:masterData.units.fields.code")}</th>
                  <th>{t("stock:masterData.units.fields.name")}</th>
                  <th>{t("stock:masterData.units.fields.dimension")}</th>
                  <th>{t("stock:masterData.common.status")}</th>
                  <th>{t("stock:masterData.common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.code}>
                    <td>
                      <div className="stock-md-row-title">{row.code}</div>
                      <div className="stock-md-row-subtitle">{row.symbol}</div>
                    </td>
                    <td>{row.name_public}</td>
                    <td>
                      {row.dimension}
                      <div className="stock-md-row-subtitle">
                        {t("stock:masterData.units.decimals", { count: row.decimal_places })}
                      </div>
                    </td>
                    <td>
                      <span className={`stock-md-badge ${row.is_active ? "" : "stock-md-badge--inactive"}`}>
                        {row.is_active ? t("stock:masterData.common.active") : t("stock:masterData.common.inactive")}
                      </span>
                    </td>
                    <td>
                      <button type="button" className="stock-md-button" onClick={() => editRow(row)}>
                        {t("common:edit", { defaultValue: "Edit" })}
                      </button>{" "}
                      <button
                        type="button"
                        className="stock-md-button stock-md-button--dangerish"
                        onClick={() => toggleActive(row)}
                        disabled={saving}
                      >
                        {row.is_active ? t("stock:masterData.common.deactivate") : t("stock:masterData.common.activate")}
                      </button>
                    </td>
                  </tr>
                ))}
                {!visibleRows.length && (
                  <tr>
                    <td colSpan="5">{loading ? t("common:loading", { defaultValue: "Loading…" }) : t("stock:masterData.units.empty")}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </main>
  );
}
